import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { contentTypes } from '~/models'
import type {
  ScriptTagModel,
  ConfigurationFileModel,
  GlobalNavigationSectionModel,
  GlobalNavigationItemModel,
  HeroBackgroundImageItemModel,
  RedirectRuleModel,
  FormattedTextModel,
  InternalLinkModel
} from '~/models'

import type { KontentPageRoute } from '~/types/KontentPageRoute'
import type { SiteConfigItem } from '~/types/SiteConfigItem'
import type { NavigationItem, NavigationMenuGroup } from '~/types/NavigationItem'
interface SiteState {
  siteConfig: ConfigurationFileModel
  fetched: boolean
  linkedItems: IContentItemsContainer
  pages: KontentPageRoute[]
  defaultBackgroundHeroImageUrl: string
  defaultBackgroundHeroImageAlt: string
  collection: string
  formattedTexts: FormattedTextModel[]
  internalLinks: InternalLinkModel[]
  siteConfigs: SiteConfigItem[]
  language: string
  redirects: RedirectRuleModel[],
  defaultLanguage: string
}

export const useSiteStore = defineStore('site', {
  state: (): SiteState => ({
    siteConfig: {} as ConfigurationFileModel,
    fetched: false,
    linkedItems: {} as IContentItemsContainer,
    pages: [] as KontentPageRoute[],
    defaultBackgroundHeroImageUrl: '',
    defaultBackgroundHeroImageAlt: '',
    collection: 'default',
    formattedTexts: [],
    internalLinks: [],
    siteConfigs: [] as SiteConfigItem[],
    language: 'en',
    redirects: [] as RedirectRuleModel[],
    defaultLanguage: 'en'
  }),
  actions: {
    setCollection(collection: string) {
      this.collection = collection
    },
    async fetchData(latest = false, lang = 'en') {
      if (!this.fetched) {
        let query = ''
        if (latest) query += '?latest=true'
        const siteResponse = await $fetch(`/api/config/${this.collection}${query}`)
        const pagesResponse = await $fetch(`/api/navigation/${this.collection}`)
        if (siteResponse.data) {
          this.siteConfigs = []
          for (const itemConfig of siteResponse.data.siteConfigs) {
            this.siteConfigs.push({
              item: itemConfig.item as ConfigurationFileModel,
              languageCode: itemConfig.languageCode,
              languageName: itemConfig.languageName,
              linkedItems: itemConfig.linkedItems as IContentItemsContainer
            })
          }
          const siteConfigItem = this.siteConfigs.find((x) => x.languageCode === lang)
          if (siteConfigItem) {
            this.siteConfig = siteConfigItem.item as ConfigurationFileModel
            this.linkedItems = siteConfigItem.linkedItems as IContentItemsContainer
          }
          this.redirects = siteResponse.data.redirects as RedirectRuleModel[]
          this.formattedTexts = siteResponse.data.formattedTexts as FormattedTextModel[]
          this.internalLinks = siteResponse.data.internalLinks as InternalLinkModel[]
        }
        if (pagesResponse.data) {
          this.pages = pagesResponse.data as KontentPageRoute[]
        }

        if (!this.defaultBackgroundHeroImageUrl) {
          if (!this.siteConfig?.elements) {
            console.warn('siteConfig.elements is undefined. Cannot determine hero background image settings.', new Error().stack)
          } else {
            const shouldUseAHeroBackgroundImage = this.siteConfig.elements.useAHeroBackgroundImage
              ? multipleChoiceToBoolean(this.siteConfig.elements.useAHeroBackgroundImage)
              : false
            if (shouldUseAHeroBackgroundImage) {
              const item = firstOrDefault(this.siteConfig.elements.customBackgroundImage.value)
              if (item) {
                this.defaultBackgroundHeroImageUrl = item.url
              } else {
                const heroBgItem = firstOrDefault(this.siteConfig.elements.heroBackgroundImage.value)

                if (heroBgItem) {
                  const response = await $fetch(`/api/herodefaults/${heroBgItem.codename}`)
                  if (response.data) {
                    const item = response.data.item as HeroBackgroundImageItemModel
                    if (item) {
                      const img = firstOrDefault(item.elements.backgroundHeroImage.value)
                      //this.defaultBackgroundHeroImageAlt = item.elements.altText.value;

                      if (img) {
                        this.defaultBackgroundHeroImageUrl = img.url
                      }
                    }
                  }
                }
              }
            }
          }
        }

        this.fetched = true
      }
    },
    async refreshSiteData() {
      await this.fetchData(true)
    },
    switchLanguage(code: string) {
      this.language = code
      this.siteConfig = this.siteConfigs.find((x) => x.languageCode === code)
        ?.item as ConfigurationFileModel
      this.linkedItems = this.siteConfigs.find((x) => x.languageCode === code)
        ?.linkedItems as IContentItemsContainer
    }
  },
  getters: {
    scriptTags: (state) => {
      const tags: ScriptTagModel[] = []

      if (state.fetched && state.siteConfig?.elements) {
        for (const item of state.siteConfig.elements.scriptTags.value) {
          const linkedItem = state.linkedItems?.[item] as ScriptTagModel
          if (!linkedItem) continue
          tags.push(linkedItem)
        }
      }

      return tags
    },
    getListAs:
      (state) =>
      <T>(codenames: string[]) => {
        if (codenames && codenames.length > 0) {
          return codenames.map((codename) => state.linkedItems?.[codename] as T)
        }

        return []
      },
    getLinkedItem:
      (state) =>
      <T>(codename: string) => {
        return state.linkedItems?.[codename] as T
      },
    getUrlByCodename: (state) => (codename: string) => {
      if (!state.fetched) return ''
      const page = state.pages.find((x) => x.codename === codename && x.lang === state.language)
      return page?.path ?? ''
    },
    getUrlByItemId: (state) => (itemId: string, language = 'en') => {
      if (!state.fetched) return ''
      const page = state.pages.find((x) => x.itemid === itemId && x.lang === language)
      return page?.path ?? ''
    },
    getPage: (state) => (urlPath: string, itemId?: string | null, collection?: string | null) => {
      if (!state.fetched) return null
      if (itemId) {
        return state.pages.find((x) => x.itemid === itemId)
      } else if (collection) {
        return state.pages.find((x) => x.collection === collection && x.path === urlPath)
      }
      return state.pages.find((x) => x.path === urlPath)
    },
    getHeaderNavigationItems: (state) => (): NavigationItem[] => {
      if (!state.fetched || !state.siteConfig?.elements) return []

      const navigationItems: NavigationItem[] = []

      if (state.siteConfig.elements.navigationHeader?.value?.length > 0) {
        for (const item of state.siteConfig.elements.navigationHeader.value) {
          const linkedItem = state.linkedItems?.[item]
          if (!linkedItem) continue

          let url = ''
          if (linkedItem.system.type === contentTypes.global_navigation_item.codename) {
            const navigationItem = linkedItem as GlobalNavigationItemModel
            if (navigationItem.elements.page.value.length > 0) {
              const pageCodename = navigationItem.elements.page.value[0]
              //@ts-ignore
              url = state.getUrlByCodename(pageCodename)
            } else {
              url = navigationItem.elements.externalUrl.value
            }

            navigationItems.push({
              title: linkedItem.elements.title.value,
              url: url,
              type: linkedItem.system.type,
              internalLink: linkedItem.elements.internalLink?.value || null,
              subItems: []
            } as NavigationItem)
          } else if (linkedItem.system.type === contentTypes.global_navigation_section.codename) {
            // Legacy behavior: flatten sections if groups API isn't used by the caller
            const navigationSection = linkedItem as GlobalNavigationSectionModel
            const subItems = getSubItems(navigationSection)
            if (subItems && subItems.length > 0) {
              navigationItems.push(...subItems)
            }
          }
        }
      }

      function getSubItems(section: GlobalNavigationSectionModel) {
        let subItems: NavigationItem[] = []
        if (section.elements.navigationItems && section.elements.navigationItems.value.length > 0) {
          for (const codename of section.elements.navigationItems.value) {
            const linkedItem = state.linkedItems?.[codename]
            if (!linkedItem) continue

            let url = ''
            if (linkedItem.system.type === contentTypes.global_navigation_item.codename) {
              const navigationItem = linkedItem as GlobalNavigationItemModel
              if (navigationItem.elements.page.value.length > 0) {
                const pageCodename = navigationItem.elements.page.value[0]
                //@ts-ignore
                url = state.getUrlByCodename(pageCodename)
              } else {
                url = navigationItem.elements.externalUrl.value
              }
              subItems.push({
                title: linkedItem.elements.title.value,
                url: url,
                type: linkedItem.system.type,
                internalLink: linkedItem.elements.internalLink?.value || null,
                subItems: []
              } as NavigationItem)
            } else {
              const navigationSection = linkedItem as GlobalNavigationSectionModel
              subItems.push({
                title: navigationSection.elements.title.value,
                url: url,
                type: navigationSection.system.type,
                subItems: getSubItems(navigationSection)
              } as NavigationItem)
            }
          }
          return subItems
        }
      }


      return navigationItems
    },
    // Groups header navigation by first-level Global Navigation Section and associates each group with its Home Page
    getHeaderNavigationGroups: (state) => (): NavigationMenuGroup[] => {
      if (!state.fetched || !state.siteConfig?.elements) return []

      const groups: NavigationMenuGroup[] = []

      const header = state.siteConfig.elements.navigationHeader?.value ?? []
      if (header.length === 0) return groups

      for (const itemCodename of header) {
        const linkedItem = state.linkedItems?.[itemCodename]
        if (!linkedItem) continue

        if (linkedItem.system.type === contentTypes.global_navigation_section.codename) {
          const section = linkedItem as GlobalNavigationSectionModel

          // Find the homepage-linked navigation item inside the section
          let homepageCodename = ''
          let homepageTitle = section.elements.title.value
          let homepageUrl = ''
          let homepageId = ''

          const sectionItems = section.elements.navigationItems?.value ?? []
          for (const codename of sectionItems) {
            const li = state.linkedItems?.[codename]
            if (!li) continue
            if (li.system.type === contentTypes.global_navigation_item.codename) {
              const navItem = li as GlobalNavigationItemModel
              if (navItem.elements.page.value.length > 0) {
                const pageCodename = navItem.elements.page.value[0]
                // If we can verify the page type is homepage, prefer it; else still accept the first page as homepage per spec assumption
                const pageItem = state.linkedItems?.[pageCodename]
                const isHomepage = pageItem?.system?.type === contentTypes.homepage.codename
                if (isHomepage || !homepageCodename) {
                  homepageId = pageItem.system.id
                  homepageCodename = pageCodename
                  homepageTitle = li.elements.title.value
                  // @ts-ignore
                  homepageUrl = (state as any).getUrlByCodename?.(pageCodename) || ''
                  if (isHomepage) break
                }
              }
            }
          }

          // Build items for the group (flattened second-level), excluding the homepage nav item itself if it maps to homepageUrl
          const items: NavigationItem[] = []
          const flattened = getSubItems(section)
          for (const it of flattened) {
            if (homepageUrl && it.url === homepageUrl) continue
            items.push(it)
          }

          groups.push({
            homepageId,
            homepageCodename,
            homepageTitle,
            homepageUrl,
            items
          })
        }
      }

      return groups

      function getSubItems(section: GlobalNavigationSectionModel) {
        let subItems: NavigationItem[] = []
        if (section.elements.navigationItems && section.elements.navigationItems.value.length > 0) {
          for (const codename of section.elements.navigationItems.value) {
            const linkedItem = state.linkedItems?.[codename]
            if (!linkedItem) continue

            let url = ''
            if (linkedItem.system.type === contentTypes.global_navigation_item.codename) {
              const navigationItem = linkedItem as GlobalNavigationItemModel
              if (navigationItem.elements.page.value.length > 0) {
                const pageCodename = navigationItem.elements.page.value[0]
                // @ts-ignore
                url = (state as any).getUrlByCodename?.(pageCodename) || ''
              } else {
                url = navigationItem.elements.externalUrl.value
              }
              subItems.push({
                title: linkedItem.elements.title.value,
                url: url,
                type: linkedItem.system.type,
                internalLink: linkedItem.elements.internalLink?.value || null,
                subItems: []
              } as NavigationItem)
            } else {
              const navigationSection = linkedItem as GlobalNavigationSectionModel
              subItems.push({
                title: navigationSection.elements.title.value,
                url: url,
                type: navigationSection.system.type,
                subItems: getSubItems(navigationSection)
              } as NavigationItem)
            }
          }
          return subItems
        }
        return subItems
      }
    },
    getFooterNavigationItems: (state) => (): NavigationItem[] => {
      if (!state.fetched || !state.siteConfig?.elements) return []

      const navigationItems: NavigationItem[] = []

      if (state.siteConfig.elements.navigationFooter?.value?.length > 0) {
        for (const item of state.siteConfig.elements.navigationFooter.value) {
          const linkedItem = state.linkedItems?.[item]
          if (!linkedItem) continue

          let url = ''
          if (linkedItem.system.type === contentTypes.global_navigation_item.codename) {
            const navigationItem = linkedItem as GlobalNavigationItemModel
            if (navigationItem.elements.page.value.length > 0) {
              const pageCodename = navigationItem.elements.page.value[0]
              //@ts-ignore
              url = state.getUrlByCodename(pageCodename)
            } else {
              url = navigationItem.elements.externalUrl.value
            }

            navigationItems.push({
              title: linkedItem.elements.title.value,
              url: url,
              type: linkedItem.system.type,
              internalLink: linkedItem.elements.internalLink?.value || null,
              subItems: []
            } as NavigationItem)
          } else if (linkedItem.system.type === contentTypes.global_navigation_section.codename) {
            const navigationSection = linkedItem as GlobalNavigationSectionModel
            navigationItems.push({
              title: navigationSection.elements.title.value,
              url: url,
              type: navigationSection.system.type,
              subItems: getSubItems(navigationSection)
            } as NavigationItem)
          }
        }
      }

      function getSubItems(section: GlobalNavigationSectionModel) {
        let subItems: NavigationItem[] = []
        if (section.elements.navigationItems && section.elements.navigationItems.value.length > 0) {
          for (const codename of section.elements.navigationItems.value) {
            const linkedItem = state.linkedItems?.[codename]
            if (!linkedItem) continue

            let url = ''
            if (linkedItem.system.type === contentTypes.global_navigation_item.codename) {
              const navigationItem = linkedItem as GlobalNavigationItemModel
              if (navigationItem.elements.page.value.length > 0) {
                const pageCodename = navigationItem.elements.page.value[0]
                //@ts-ignore
                url = state.getUrlByCodename(pageCodename)
              } else {
                url = navigationItem.elements.externalUrl.value
              }
              subItems.push({
                title: linkedItem.elements.title.value,
                url: url,
                type: linkedItem.system.type,
                internalLink: linkedItem.elements.internalLink?.value || null,
                subItems: []
              } as NavigationItem)
            } else {
              const navigationSection = linkedItem as GlobalNavigationSectionModel
              subItems.push({
                title: navigationSection.elements.title.value,
                url: url,
                subItems: getSubItems(navigationSection)
              } as NavigationItem)
            }
          }
          return subItems
        }
      }

      return navigationItems
    },
    getFormattedText: (state) => (itemId: string) => {
      if (!state.fetched) return ''
      const item = state.formattedTexts.find((x) => x.system.id === itemId)
      return item
    },    
    getInternalLink: (state) => (itemId: string) => {
      if (!state.fetched) return ''
      const item = state.internalLinks.find((x) => x.system.id === itemId)
      return item
    },
    getInternalLinkByCodename: (state) => (codename: string) => {
      if (!state.fetched) return ''
      const item = state.internalLinks.find((x) => x.system.codename === codename)
      return item
    },
    languages: (state) => {
      if (!state.fetched) return []
      return state.siteConfigs.map((x) => {
        return {
          name: x.languageName,
          code: x.languageCode
        }
      })
    },
    getRedirectPath: (state) => (sourcePath: string) => {
      if (!state.fetched) return ''
      const redirect = state.redirects.find(
        (x) => x.elements.sourcePath.value.toLocaleLowerCase() === sourcePath.toLocaleLowerCase()
      )
      return redirect?.elements.targetPathOrUrl.value ?? undefined
    }
  }
})

// TypeScript interface for the extensible initialization object
/**
 * The `initialization` property is not placed in `$state` because it is intended for
 * managing async initialization logic (such as tracking a fetch promise) that should not
 * be persisted, serialized, or trigger reactivity in the store's state. This keeps
 * initialization concerns separate from the application's reactive state, avoiding
 * unnecessary reactivity and potential side effects.
 * It will cause a 500 error if placed in `$state` because it is not serializable.
 */
export interface SiteStoreWithInit extends ReturnType<typeof useSiteStore> {
  initialization?: {
    fetchKontentData?: Promise<any>
    // Add other async init keys here as needed
  }
}
